// ─── Lancement d'outils sur le poste du technicien (Fonxtio Tech) ─────
//
// Le bouton web demande un TICKET à usage unique (60 s) à la plateforme, puis
// ouvre un lien `fonxtio://launch/?server=…&ticket=…` : l'application Fonxtio
// Tech du poste l'échange contre les paramètres (hôte, port, identifiants) et
// lance WinBox. L'URL ne porte jamais de secret.
//
// Générique : le même chemin servira RDP, SSH, VNC (`/platform/launch/{outil}/{id}`).
//
// Pourquoi `window.location.href` et non `window.open` : un lien de protocole
// personnalisé n'ouvre aucune page, le navigateur délègue à l'application
// enregistrée. Avec `href` on reste sur le dashboard ; avec `open`, un onglet
// vide resterait derrière.

import { useState } from 'react';
import { api } from './api.jsx';

// Le lien de téléchargement ne change pas pendant la session : une requête.
let configPromise = null;
export function getLaunchConfig() {
  if (!configPromise) {
    configPromise = api.get('/platform/launch/config').then((r) => {
      // Un échec ne doit pas être mémorisé : on retentera au prochain lancement.
      if (!r) configPromise = null;
      return r;
    });
  }
  return configPromise;
}

const ERROR_BY_STATUS = {
  403: "Vous n'avez pas le droit d'ouvrir une session de contrôle à distance sur cet appareil.",
  404: "Appareil introuvable ou lancement non disponible pour cet appareil.",
};

// ─── Notice temporaire ───
// Construite directement dans le DOM : l'appel part d'un clic dans n'importe
// quelle page, et monter un composant global demanderait de toucher à la coquille
// du tableau de bord. Les classes sont des jetons sémantiques (thème clair/sombre).
let currentNotice = null;
function showNotice({ text, tone = 'info', downloadUrl = null, ms = 12000 }) {
  if (currentNotice) {
    clearTimeout(currentNotice.timer);
    currentNotice.el.remove();
    currentNotice = null;
  }
  const el = document.createElement('div');
  el.setAttribute('role', 'status');
  el.className =
    'fixed bottom-5 right-5 z-[1100] max-w-[380px] rounded-xl border px-4 py-3 text-[12.5px] shadow-2xl bg-card ' +
    (tone === 'error' ? 'border-crit/40 text-crit' : 'border-blue-500/40 text-fg-2');
  const p = document.createElement('div');
  p.textContent = text;
  el.appendChild(p);
  if (downloadUrl) {
    const hint = document.createElement('div');
    hint.className = 'mt-1.5 text-[11.5px] text-muted';
    hint.appendChild(document.createTextNode('Rien ne s’ouvre ? '));
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.className = 'text-blue-400 hover:underline font-semibold';
    a.textContent = 'Télécharger Fonxtio Tech';
    hint.appendChild(a);
    el.appendChild(hint);
  }
  const close = document.createElement('button');
  close.type = 'button';
  close.setAttribute('aria-label', 'Fermer');
  close.className = 'absolute top-1.5 right-2.5 bg-transparent border-none text-muted cursor-pointer text-sm';
  close.textContent = '×';
  close.onclick = () => {
    clearTimeout(timer);
    el.remove();
    if (currentNotice?.el === el) currentNotice = null;
  };
  el.appendChild(close);
  document.body.appendChild(el);
  const timer = setTimeout(() => {
    el.remove();
    if (currentNotice?.el === el) currentNotice = null;
  }, ms);
  currentNotice = { el, timer };
}

/**
 * Demande un ticket de lancement puis ouvre le lien `fonxtio://`.
 * `path` : route sous /api, ex. `/platform/launch/winbox/42`.
 * Rend `{ ok, error? }` ; l'erreur est aussi affichée en notice.
 */
export async function launchTool(path) {
  let res;
  try {
    res = await api.fetch(path, { method: 'POST', body: '{}' });
  } catch {
    const error = 'Le serveur est injoignable.';
    showNotice({ text: error, tone: 'error' });
    return { ok: false, error };
  }
  let json = null;
  try {
    const t = await res.text();
    json = t ? JSON.parse(t) : null;
  } catch {
    /* corps non JSON */
  }
  if (!res.ok || !json?.url) {
    const error = json?.error || ERROR_BY_STATUS[res.status] || "Échec de l'ouverture (erreur " + res.status + ').';
    showNotice({ text: error, tone: 'error', ms: 8000 });
    return { ok: false, error };
  }
  // Le lien est ouvert AVANT d'attendre la config de téléchargement : plus rien
  // d'asynchrone entre la réponse et la navigation.
  window.location.href = json.url;
  const cfg = await getLaunchConfig();
  showNotice({ text: 'Ouverture via Fonxtio Tech…', downloadUrl: cfg?.downloadUrl || null });
  return { ok: true };
}

/** Bouton réutilisable : un clic = un lancement, désactivé pendant la demande. */
export function LaunchButton({ path, icon: Icon, children, title, className = '', disabled = false }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      disabled={busy || disabled}
      title={title}
      onClick={async (e) => {
        e.stopPropagation();
        setBusy(true);
        await launchTool(path);
        setBusy(false);
      }}
      className={
        className ||
        'bg-blue-500 border-none rounded-lg px-3.5 py-2 text-white cursor-pointer text-xs font-semibold hover:bg-blue-600 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-1.5'
      }
    >
      {Icon && <Icon size={14} strokeWidth={1.75} />}
      {busy ? 'Ouverture…' : children}
    </button>
  );
}
